/**
 * Lineage Service
 *
 * Reads from the lineage_events table to provide split/merge/supersede
 * provenance for items. This is the authoritative source for item lineage.
 */

import { getSafeSupabaseClient } from './supabaseSync';

/** DB row shape */
export interface LineageEventRow {
  id: string;
  event_type: 'split' | 'merge' | 'supersede' | 'retire' | 'fork';
  source_item_ids: string[];
  target_item_ids: string[];
  performed_by: string | null;
  created_at: string;
  rationale: string | null;
  metadata: Record<string, unknown> | null;
}

export type LineageEventType = 'split' | 'merge' | 'supersede' | 'retire' | 'fork';

/** Client-facing event (camelCase) for LineageHistory and LineageSummary */
export interface LineageEvent {
  id: string;
  eventType: LineageEventType;
  sourceItemIds: string[];
  targetItemIds: string[];
  performedBy: string;
  createdAt: string;
  rationale?: string | null;
  metadata?: Record<string, unknown>;
}

function mapRowToEvent(row: LineageEventRow): LineageEvent {
  return {
    id: row.id,
    eventType: row.event_type as LineageEventType,
    sourceItemIds: row.source_item_ids || [],
    targetItemIds: row.target_item_ids || [],
    performedBy: row.performed_by ?? '',
    createdAt: row.created_at,
    rationale: row.rationale ?? undefined,
    metadata: row.metadata ?? undefined,
  };
}

/**
 * Fetch all lineage events where a given item appears as source or target (raw rows).
 */
export async function getLineageForItem(itemId: string): Promise<LineageEventRow[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  const [asSource, asTarget] = await Promise.all([
    supabase
      .from('lineage_events')
      .select('*')
      .contains('source_item_ids', [itemId])
      .order('created_at', { ascending: false }),
    supabase
      .from('lineage_events')
      .select('*')
      .contains('target_item_ids', [itemId])
      .order('created_at', { ascending: false }),
  ]);

  const seen = new Set<string>();
  const events: LineageEventRow[] = [];
  for (const row of [...(asSource.data || []), ...(asTarget.data || [])]) {
    if (!seen.has(row.id)) {
      seen.add(row.id);
      events.push(row as LineageEventRow);
    }
  }
  events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return events;
}

/**
 * Get full lineage for an item (camelCase, both directions, sorted chronologically).
 */
export async function getFullLineage(itemId: string): Promise<{
  asSource: LineageEvent[];
  asTarget: LineageEvent[];
  all: LineageEvent[];
}> {
  const rows = await getLineageForItem(itemId);
  const asSource: LineageEvent[] = [];
  const asTarget: LineageEvent[] = [];
  const allMap = new Map<string, LineageEvent>();
  for (const row of rows) {
    const event = mapRowToEvent(row);
    allMap.set(event.id, event);
    if ((row.source_item_ids || []).includes(itemId)) asSource.push(event);
    if ((row.target_item_ids || []).includes(itemId)) asTarget.push(event);
  }
  const all = Array.from(allMap.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return { asSource, asTarget, all };
}

/**
 * Human-readable description of a lineage event.
 */
export function getLineageEventDescription(event: LineageEvent): string {
  const n = event.sourceItemIds.length;
  const m = event.targetItemIds.length;
  switch (event.eventType) {
    case 'split':
      return `Split into ${m} item${m !== 1 ? 's' : ''}`;
    case 'merge':
      return `Merged from ${n} item${n !== 1 ? 's' : ''}`;
    case 'supersede':
      return 'Superseded by new version';
    case 'retire':
      return 'Retired';
    case 'fork':
      return 'Forked as independent copy';
    default:
      return event.eventType;
  }
}
